import React from 'react';
import { Product } from '../../services/api/domains/products/types';
import { modalStyles as styles } from './styles';
import useEditableModal, { ModalMode } from './hooks/useEditableModal';
import ModalFieldRenderer from './ModalFieldRenderer';
import {
  toModalFields,
  getAlwaysDisabledFields,
} from '../../domains/products/adapters';
import { Manufacturer } from 'services/api/domains/manufacturers/types';

interface ProductEditProps {
  product: Product;
  initialMode?: 'EDIT' | 'VIEW';
  save: (editedProduct: Product) => void;
  close: () => void;
  allManufacturers: Manufacturer[];
}

const ProductModal: React.FC<ProductEditProps> = ({
  product,
  initialMode = 'VIEW',
  save,
  close,
  allManufacturers,
}) => {
  // Use the hook for all modal logic
  const {
    editedEntity: editedProduct,
    setEditedEntity: setEditedProduct,
    modalMode,
    focusedField,
    setFocusedField,
    handleSave,
    handleClose,
    isFieldDisabled,
    getFloatingLabelStyle,
    getFloatingInputStyle,
    getSaveButtonStyle,
    getSaveButtonHoverStyle,
    getSaveButtonFocusStyle,
    getCloseButtonStyle,
    getCloseButtonHoverStyle,
    getCloseButtonFocusStyle,
    getSaveButtonText,
    getCloseButtonText,
  } = useEditableModal({
    entity: product,
    initialMode,
    onSave: save,
    onClose: close,
    alwaysDisabledFields: getAlwaysDisabledFields(),
    styles,
  });

  return (
    <div style={styles.modalOverlayStyle}>
      <div style={styles.modalContentStyle}>
        <div style={styles.modalHeaderStyle}>
          <h2 style={styles.modalTitleStyle}>
            {modalMode === 'EDIT' ? 'Edit Product' : 'Product Details'}
          </h2>
          <button
            onClick={handleClose}
            style={styles.modalCloseXStyle}
            onMouseEnter={e =>
              Object.assign(e.currentTarget.style, styles.modalCloseXHoverStyle)
            }
            onMouseLeave={e =>
              Object.assign(e.currentTarget.style, styles.modalCloseXStyle)
            }>
            ×
          </button>
        </div>

        <div style={styles.modalBodyStyle}>
          {/* All other product fields */}
          <ModalFieldRenderer
            fields={toModalFields({
              manufacturers: allManufacturers,
            })}
            entity={editedProduct}
            setEntity={setEditedProduct}
            mode={modalMode}
            focusedField={focusedField}
            setFocusedField={setFocusedField}
            isFieldDisabled={isFieldDisabled}
            getFloatingLabelStyle={getFloatingLabelStyle}
            getFloatingInputStyle={getFloatingInputStyle}
            styles={styles}
            missingMessage="Unknown"
          />
        </div>

        <div style={styles.modalFooterStyle}>
          <button
            onClick={handleClose}
            style={getCloseButtonStyle()}
            onMouseEnter={e =>
              Object.assign(e.currentTarget.style, getCloseButtonHoverStyle())
            }
            onMouseLeave={e =>
              Object.assign(e.currentTarget.style, getCloseButtonStyle())
            }
            onFocus={e =>
              Object.assign(e.currentTarget.style, getCloseButtonFocusStyle())
            }
            onBlur={e =>
              Object.assign(e.currentTarget.style, getCloseButtonStyle())
            }>
            {getCloseButtonText()}
          </button>
          <button
            onClick={handleSave}
            style={getSaveButtonStyle()}
            onMouseEnter={e =>
              Object.assign(e.currentTarget.style, getSaveButtonHoverStyle())
            }
            onMouseLeave={e =>
              Object.assign(e.currentTarget.style, getSaveButtonStyle())
            }
            onFocus={e =>
              Object.assign(e.currentTarget.style, getSaveButtonFocusStyle())
            }
            onBlur={e =>
              Object.assign(e.currentTarget.style, getSaveButtonStyle())
            }>
            {getSaveButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ProductModal);
export type { ModalMode };
